import { Avatar, Button, Dropdown, Space, Tooltip } from 'antd'
import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../actions'
import { routes } from '../../../consts'
import { eventTypes } from '../../../consts/eventConsts'
import $t from '../../../i18n'
import { customClickTracking } from '../../../utils/eventUtils'
import NotInsuredLabel from '../../common/NotInsuredLabel'
import { isTaskCompleted } from '../../../utils/onBoardingUtils'
import { DOWNLOAD_APP } from '../../../consts/OnBoardingConsts'
import InviteToAppLabelAction from '../../common/InviteToAppLabelAction'
import { getIsHmoMember } from '../../../utils/hmoUtils'
import { isMemberMinor } from '../../../utils/familyUtils'
import EnrollmentYearsDropdown from '../../common/EnrollmentYearsDropdown'
import './InternalHeader.css'
const InternalHeader = ({
  displayEnrollmentYearsDropdown,
  hideProfile,
  displayManageFamily,
  isFamilyHidden,
  isNotEnsuredDisabled
}) => {
  const dispatch = useDispatch()
  const manageFamilyRef = useRef()
  const { isUserDetailsLoading, userDetails } = useSelector(
    state => state.profile
  )
  const isOwnerHmo = !!getIsHmoMember(userDetails)
  const isCallActive = useSelector(state => state.videoCall.isCallActive)
  const { firstName, lastName } = userDetails || {}

  const { selectedMember, members, activeMembers } = useSelector(
    state => state.account
  )

  const isSelectedMemberHmo = getIsHmoMember(selectedMember)

  // showing members without permissions as disabled
  // just members that didn't accept the invitation and downloaded the app
  // only hmo members!
  const disabledMembers = members
    ?.filter(
      member =>
        !activeMembers.find(
          activemember =>
            activemember?.id?.toString() === member?.id?.toString()
        ) &&
        getIsHmoMember(member) &&
        !isTaskCompleted(member?.hmo_data?.on_boarding_tasks, DOWNLOAD_APP.name)
    )
    ?.map(member => ({ ...member, isDisabled: true }))

  const allMembers = activeMembers?.concat(disabledMembers)

  const navigateToProfile = () => {
    dispatch(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.PROFILE)
    )
  }

  const onFamilyMemberSelected = memberId => {
    // memberId is represented with string, while owner id not (int)
    const isOwner = memberId === userDetails?.id?.toString()
    const data = {
      member: isOwner
        ? userDetails
        : members?.find(member => member?.id === memberId)
    }
    dispatch(
      Actions.createAction(Actions.CHOOSE_FAMILY_MEMBER, {
        member: isOwner
          ? userDetails
          : members?.find(member => member?.id === memberId)
      })
    )
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: Actions.CHOOSE_FAMILY_MEMBER,
        data: customClickTracking(Actions.CHOOSE_FAMILY_MEMBER, data)
      })
    )
  }

  const activeMembersWithInsuredDisabled = allMembers.map(member =>
    getIsHmoMember(member) ? member : { ...member, isDisabled: true }
  )

  const activeMembersWithOwnerAndFamilyOption = [
    { firstName: $t('common.family'), id: null },
    { firstName: firstName, id: userDetails?.id }
  ].concat(isNotEnsuredDisabled ? activeMembersWithInsuredDisabled : allMembers)

  const activeMembersWithOwnerOption = [
    { firstName: firstName, id: userDetails?.id }
  ].concat(isNotEnsuredDisabled ? activeMembersWithInsuredDisabled : allMembers)

  const activeMemberToDisplay = isFamilyHidden
    ? activeMembersWithOwnerOption
    : activeMembersWithOwnerAndFamilyOption

  // if the user is without family, we don't show the family manage label
  const isWithoutFamily = !activeMemberToDisplay?.length || !allMembers?.length

  const shouldShowHeader = firstName && lastName && !isUserDetailsLoading

  const memberList = {
    items: activeMemberToDisplay?.map(member => ({
      label: (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
          key={member?.id}
        >
          <div style={{ flex: 1 }}> {member?.firstName}</div>
          {member?.id &&
            member?.isDisabled &&
            getIsHmoMember(member) &&
            !isMemberMinor(member) && (
              <InviteToAppLabelAction patientId={member?.id} />
            )}
          {member?.id && <NotInsuredLabel patientId={member?.id} />}
        </div>
      ),
      key: member?.id,
      disabled: member?.isDisabled
    })),
    onClick: ({ key }) => onFamilyMemberSelected(key)
  }

  const getTooltipTitle = infoText => {
    return (
      <div className='tooltip'>
        <div className='tooltip-title-body-header light'>{infoText}</div>
      </div>
    )
  }

  const getManageFamilyAction = () => {
    return (
      <div
        className={
          !!isSelectedMemberHmo || !selectedMember || !isOwnerHmo
            ? 'manage-family'
            : 'manage-family not-insured'
        }
        data-hidden={isWithoutFamily}
        ref={manageFamilyRef}
      >
        <Tooltip
          title={getTooltipTitle(
            $t('homeScreen.notInsuredInfo', {
              name: selectedMember?.firstName
            })
          )}
          placement='bottom'
          color={'var(--color_primary_t600)'}
        >
          {selectedMember && !isSelectedMemberHmo && isOwnerHmo && (
            <i className='icon-oinfo'></i>
          )}
        </Tooltip>
        <div className='title'> {$t('homeScreen.familyManage')}</div>
        <Dropdown
          menu={memberList}
          className={
            !!isSelectedMemberHmo || !selectedMember || !isOwnerHmo
              ? 'button-dropdown-family'
              : 'button-dropdown-family not-insured'
          }
          getPopupContainer={() => manageFamilyRef?.current}
          overlayClassName='family-manage-dropdown'
          placement='bottomRight'
        >
          <Button type='primary'>
            <Space>
              {/*in case no selected member, show 'family' word. but in case the family hidden flag is on, show the userdetails first name */}
              {selectedMember?.firstName || isFamilyHidden
                ? selectedMember?.firstName
                : $t('common.family')}
              <span className='icon-arrow-bold-down' />
            </Space>
          </Button>
        </Dropdown>
      </div>
    )
  }

  const getProfileAvatar = () => (
    <Avatar
      className='profile-avatar'
      size='large'
      onClick={isCallActive ? () => {} : navigateToProfile}
    >
      {firstName?.toUpperCase()?.slice(0, 1) +
        lastName?.toUpperCase()?.slice(0, 1)}
    </Avatar>
  )

  return (
    <div className='internal-header'>
      {shouldShowHeader && (
        <div className='internal-header-container'>
          {displayEnrollmentYearsDropdown && <EnrollmentYearsDropdown />}
          {!isCallActive && displayManageFamily && getManageFamilyAction()}
          {!hideProfile && getProfileAvatar()}
        </div>
      )}
    </div>
  )
}

export default InternalHeader
